import { useRef, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'

import {
  // getRecentAddress,
  postUserAddressCalculate,
  postGuestAddressCalculate,
  getAddressList,
} from '../../api/orderHistory'
import { getFishingProfile } from '../../api/login'
import { getOrderSheet } from '../../api/cart'
import { getSubmitCoupons } from '../../api/coupons'
import { getAccessToken } from '../../api/auth'
import { getParsedLocalStorageItem } from '../../js/script'
import { handlePostMessage } from '../../js/postMessage'
import { trackBeginCheckout, trackAddPaymentInfo, toGTMItems, trackAddShippingInfo } from '../../js/analytics'

import { PageTitle } from '../title/PageTitle'
import Hr8 from '../hr/Hr8'
import OrderUserInfoSection from './OrderUserInfoSection'
import OrderAddressSection from './OrderAddressSection'
import OrderProductSection from './OrderProductSection'
import OrderAccumulationSection, { OrderAccumulationSectionHandle } from './OrderAccumulationSection'
import PaymentMethodSection from './PaymentMethodSection'
import OrderSummarySection from './OrderSummarySection'

import { useCommonContext } from '../provider/CommonProvider'
import { useAppContext } from '../provider/AppProvider'
import { useMyContext } from '../provider/MyProvider'

import { UserProfile } from '../../@types/auth'
import { PaymentInfoItem, AddressItem } from '../../@types/order'
import {
  Nullable,
  GetOrderSheetsOrderSheetNoResponse,
  GetOrderSheetsOrderSheetNoCouponsResponse,
  OrderProduct,
  PaymentInfo,
} from '@shopby/shop-sdk'

export type OrderProductWithUsable = OrderProduct & {
  accumulationUsable?: boolean
  couponUsable?: boolean
  categoryNo?: number
  categoryNos?: number[]
}

type OrderUserInfoSectionHandle = {
  validate: () => boolean
  getValues: () => {
    name: string
    phone: string
    email: string
    password: string
    confirmPassword: string
  }
}

type OrderAddressSectionHandle = {
  validate: () => boolean
  getValues: () => {
    receiverName: string
    receiverZipCd: string
    receiverAddress_1: string
    receiverAddress_2: string
    receiverPhone: string
    receiverMemo: string
  }
}

export type GetOrderSheetsOrderSheetNoResponseWithMyInfo = GetOrderSheetsOrderSheetNoResponse & {
  // myPayInfo: Nullable<UpdateMyPayInfo>
  myPayInfo: Nullable<any>
}

const defaultPaymentInfo: PaymentInfo = {
  paymentAmt: 0,
  cartAmt: 0,
  productAmt: 0,
  deliveryCouponAmt: 0,
  accumulationAmtWhenBuyConfirm: 0,
  usedAccumulationAmt: 0,
  remoteDeliveryAmt: 0,
  productCouponAmt: 0,
  customsDuty: 0,
  totalImmediateDiscountAmt: 0,
  availableMaxAccumulationAmt: 0,
  accumulationAmt: 0,
  deliveryAmtOnDelivery: 0,
  salesTaxAmt: 0,
  isAvailableAccumulation: false,
  totalStandardAmt: 0,
  remoteDeliveryAmtOnDelivery: 0,
  cartCouponAmt: 0,
  minAccumulationLimit: 0,
  minPriceLimit: 0,
  deliveryAmt: 0,
  totalAdditionalDiscountAmt: 0,
}

const NewOrder = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null) // 사용자 프로필 정보
  // const [userDefaultAddress, setUserDefaultAddress] = useState<RecentAddressType[]>([]) //기본 배송지
  const [userDefaultAddress, setUserDefaultAddress] = useState<AddressItem | null>(null) //기본 배송지
  const [orderItems, setOrderItems] = useState<OrderProductWithUsable[]>([]) // 주문 상품 정보
  const [coupons, setCoupons] = useState<GetOrderSheetsOrderSheetNoCouponsResponse | null>(null) // 쿠폰 정보

  const [isPointChanged, setIsPointChanged] = useState<boolean>(false) // 포인트 변경 여부
  const [accumulationUseAmt, setAccumulationUseAmt] = useState<number>(0) // 입력 완료된 적립금

  const [selectPayType, setSelectPayType] = useState('신용 / 체크카드')
  const [orderData, setOrderData] = useState<GetOrderSheetsOrderSheetNoResponseWithMyInfo | null>(null) // 주문 상품 정보
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>(defaultPaymentInfo) //주문 가격
  const [cardInfo, setCardInfo] = useState<PaymentInfoItem | null>(null) //어바웃피싱 페이 정보

  const [isAgreePayment, setIsAgreePayment] = useState<boolean>(false) // 결제 약관 동의 여부
  const [isAgreeNonMemberPrivacy, setIsAgreeNonMemberPrivacy] = useState<boolean>(false) // 비회원 개인정보 수집 동의 여부
  const [showAgreement, setShowAgreement] = useState<boolean>(false) // 약관 동의 팝업 표시 여부

  const [isAddressReady, setIsAddressReady] = useState(false)
  const [isProductReady, setIsProductReady] = useState(false)
  const [isAccumulationReady, setIsAccumulationReady] = useState(false)

  const { hasAccessToken } = useCommonContext()
  const { isAppView } = useAppContext()
  const { findCategory, categories, setToastMessage, setIsToast } = useMyContext()

  const params = useParams()
  const navigate = useNavigate()

  const orderInfoRef = useRef<OrderUserInfoSectionHandle>(null)
  const orderAddressRef = useRef<OrderAddressSectionHandle>(null)
  const orderProductRef = useRef<{ getCoupons: () => { productCoupon: any; cartCoupon: any } }>(null)
  const accumulationInputRef = useRef<OrderAccumulationSectionHandle>(null)

  // GA 이벤트 한번 전달하기 위한 Ref
  const trackedRef = useRef(false)
  const [isTrackingReady, setIsTrackingReady] = useState(false)

  const PG_TYPE_MAP: Record<string, string> = {
    MY_PAY: 'MY_PAY',
    KAKAO_PAY: 'KAKAO_PAY',
    NAVER_EASY_PAY: 'NAVER_EASY_PAY',
    CREDIT_CARD: 'INICIS',
    '신용 / 체크카드': 'INICIS',
  }

  const PAY_TYPE_MAP: Record<string, string> = {
    '어바웃피싱 페이': 'MY_PAY',
    카카오페이: 'KAKAO_PAY',
    네이버페이: 'NAVER_EASY_PAY',
    '신용 / 체크카드': 'CREDIT_CARD',
  }

  const validateOrderForm = () => {
    // 주문자 정보 validate (빨간색 표시 및 포커스 포함)
    if (!orderInfoRef.current?.validate?.()) return false

    // 배송지 정보 validate (빨간색 표시 및 포커스 포함)
    if (!orderAddressRef.current?.validate?.()) return false

    // 약관 동의
    if (!isAgreePayment) {
      alert('구매조건 확인 및 결제 진행 동의를 해주세요')
      return false
    }
    if (!hasAccessToken && !isAgreeNonMemberPrivacy) {
      alert('비회원 주문 개인정보 수집 / 이용 동의를 해주세요')
      return false
    }

    // 기타 필요한 추가 검사...
    return true
  }

  const requestPayment = ({
    selectPayType,
    cardInfo,
    option,
    myPayData,
    isAppView,
    isMobile,
    hasAccessToken,
  }: {
    selectPayType: string
    cardInfo: any
    option: any
    myPayData: any
    isAppView: boolean
    isMobile: boolean
    hasAccessToken: boolean
  }) => {
    // MY_PAY
    if (selectPayType === 'MY_PAY') {
      if (cardInfo) {
        const accesstoken = getParsedLocalStorageItem('accessToken')
        if (isAppView || isMobile) {
          const encode = encodeURIComponent(JSON.stringify(option))
          if (isAppView) {
            handlePostMessage({
              actionType: 'onLink',
              params: {
                url: `${process.env.REACT_APP_SHOP_URL}/mypaypaid?productdata=${encode}&payLogo=${JSON.stringify(
                  myPayData
                )}&accesstoken=${accesstoken}&app=true`,
              },
            })
          } else {
            const open = window.open('about:blank', '_blank')
            if (!open) return alert('팝업이 차단되어 있습니다. 팝업을 허용해주세요.')
            open.location.href = `${process.env.REACT_APP_SHOP_URL}/mypaypaid?productdata=${encode}&app=false`
          }
        } else {
          if (window.NCPPay) {
            window.NCPPay.setMyPayCssCustom(
              `https://shop-api.e-ncp.com${myPayData?.customCssUrl}`,
              null,
              `${myPayData?.payName}`,
              null
            )
            window.NCPPay?.setConfiguration({
              clientId: process.env.REACT_APP_CLIENT_ID,
              confirmUrl: `${process.env.REACT_APP_SHOP_URL}/closetest`,
              platform: isMobile ? 'MOBILE_WEB' : 'PC',
              accessToken: hasAccessToken ? getAccessToken() : '',
            })
            window.NCPPay?.reservation(option, function (response: any) {
              // 결제 완료 후 처리
              if (response?.url && response?.payType) {
                const result = response.url.split('?')
                navigate(`/orderFinish?${result[1]}&paytype=${response.payType}`)
              }
            })
          }
        }
      } else {
        alert('카드를 등록 후 사용해주세요')
      }
    }
    // KAKAO_PAY
    else if (selectPayType === 'KAKAO_PAY') {
      if (window.NCPPay) {
        window.NCPPay?.setConfiguration({
          clientId: process.env.REACT_APP_CLIENT_ID,
          confirmUrl: isMobile
            ? `${process.env.REACT_APP_SHOP_URL}/orderFinish`
            : `${process.env.REACT_APP_SHOP_URL}/closetest`,
          platform: isMobile ? 'MOBILE_WEB' : 'PC',
          accessToken: hasAccessToken ? getAccessToken() : '',
        })
        window.NCPPay?.reservation(option, function (response: any) {
          if (response?.url && response?.payType) {
            const result = response.url.split('?')
            navigate(`/orderFinish?${result[1]}&paytype=${response.payType}`)
          }
        })
      }
    }
    // 기타 결제수단
    else {
      if (window.NCPPay) {
        window.NCPPay?.setConfiguration({
          clientId: process.env.REACT_APP_CLIENT_ID,
          confirmUrl: `${process.env.REACT_APP_SHOP_URL}/orderFinish`,
          platform: isMobile ? 'MOBILE_WEB' : 'PC',
          accessToken: hasAccessToken ? getAccessToken() : '',
        })
        window.NCPPay?.reservation(option, function (response: any) {
          if (response?.url && response?.payType) {
            const result = response.url.split('?')
            navigate(`/orderFinish?${result[1]}&paytype=${response.payType}`)
          }
        })
      }
    }
  }

  // 결제 옵션 생성부
  const buildOrderOption = ({
    orderer,
    receiver,
    coupons,
    selectPayType,
    cardInfo,
    accumulationUseAmt,
    isAgreePayment,
  }: {
    orderer: any
    receiver: any
    coupons: any
    selectPayType: string
    cardInfo: any
    accumulationUseAmt: number
    isAgreePayment: boolean
  }) => {
    const getPgType = (payType: string) => PG_TYPE_MAP[payType] || 'INICIS'
    const getPayType = (payType: string) => PAY_TYPE_MAP[payType] || 'CREDIT_CARD'

    return {
      orderSheetNo: params.orderSheetNo,
      extraData: { productCoupon: coupons.productCoupon, cartCoupon: coupons.cartCoupon },
      shippingAddress: {
        addressNo: 0,
        receiverZipCd: receiver?.receiverZipCd,
        receiverAddress: receiver?.receiverAddress_1,
        receiverJibunAddress: `${receiver?.receiverAddress_1} ${receiver?.receiverAddress_2}`,
        receiverDetailAddress: receiver?.receiverAddress_2,
        receiverName: receiver?.receiverName,
        addressName: `${receiver?.receiverAddress_1 ?? ''} ${receiver?.receiverAddress_2 ?? ''}`,
        receiverContact1: receiver?.receiverPhone?.replace(/[^0-9]/g, ''),
        receiverContact2: receiver?.receiverPhone?.replace(/[^0-9]/g, '') ?? '',
        customsIdNumber: '',
        countryCd: 'KR',
        usesShippingInfoLaterInput: false,
        shippingInfoLaterInputContact: null,
        requestShippingDate: null,
        receiverCity: null,
        receiverState: null,
        receiverFirstName: null,
        receiverLastName: null,
        orderAdditionalInfo: null,
      },
      coupons: {
        cartCouponIssueNo: coupons.cartCoupon?.couponIssueNo ?? null,
        productCoupons: Object.entries(coupons.productCoupon || {}).map(([key, value]: [string, any]) => ({
          couponIssueNo: value.couponIssueNo,
          productNo: Number(key.split('_')[0]),
        })),
      },
      useDefaultAddress: false,
      saveAddressBook: true,
      orderMemo: '',
      deliveryMemo: receiver?.receiverMemo ?? '',
      member: !!hasAccessToken,
      orderer: {
        ordererName: orderer?.name,
        ordererEmail: orderer?.email ?? '',
        ordererContact1: orderer?.phone?.replace(/[^0-9]/g, ''),
        ordererContact2: '',
      },
      updateMember: false,
      tempPassword: orderer?.password ?? null,
      subPayAmt: accumulationUseAmt,
      orderTitle: '',
      pgType: getPgType(selectPayType),
      payType: getPayType(selectPayType),
      agreementTermsAgrees: [
        { isAgree: isAgreePayment, termsType: 'USE' },
        { isAgree: isAgreePayment, termsType: 'PI_COLLECTION_AND_USE_ON_ORDER' },
        { isAgree: isAgreePayment, termsType: 'PI_SELLER_PROVISION' },
        { isAgree: isAgreePayment, termsType: 'CLEARANCE_INFO_COLLECTION_AND_USE' },
        { isAgree: isAgreePayment, termsType: 'ORDER_INFO_AGREE' },
        { isAgree: isAgreePayment, termsType: 'ORDER_DEFAULT' },
      ],
      clientReturnUrl: `${process.env.REACT_APP_SHOP_URL}/closetest`,
      clientParams: {},
      inAppYn: 'Y',
      paymentAmtForVerification: 0,
      savesLastPayType: false,
      applyCashReceipt: false,
      cashReceipt: null,
      bankAccountToDeposit: null,
      remitter: null,
      shippingAddresses: [],
      rentalInfo: null,
      myPayInfo:
        selectPayType === 'MY_PAY'
          ? {
              mobileInfo: { flagPin: 'Y', hppType: null },
              accountInfo: null,
              cardInfo: {
                flagPin: 'Y',
                flagCardPoint: 'N',
                cardQuota: '00',
                couponCode: null,
                cardInterest: null,
                bankCardCode: cardInfo?.bankCardCode ?? null,
              },
              payMethod: '01',
              wpayToken: cardInfo?.payToken,
            }
          : undefined,
      customTermsNos: null,
      agreementTypes: null,
      customTermsAgrees: [
        {
          customTermsNo: 1,
          isAgree: true,
        },
      ],
    }
  }

  // 실제 결제 요청부
  const handlePurchase = () => {
    if (!validateOrderForm()) return

    // NOTE: 임시 처리. 배송지 변경 시에 해당 이벤트가 발생하여야 하나 현재 처리가 쉽지않은 구조로 인해 추가 개발시간이 필요로 임시처리함
    trackAddShippingInfo({
      isAppView,
      orderPrice: paymentInfo,
      cartArray: hasAccessToken
        ? toGTMItems(orderItems, categories, findCategory, coupons)
        : toGTMItems(orderItems, categories, findCategory),
      reserve_discount: hasAccessToken ? accumulationUseAmt : 0,
    })

    // 결제 정보 입력 트래킹
    trackAddPaymentInfo({
      isAppView,
      orderPrice: paymentInfo,
      cartArray: hasAccessToken
        ? toGTMItems(orderItems, categories, findCategory, coupons)
        : toGTMItems(orderItems, categories, findCategory),
      selectPay: selectPayType,
      pay_back: paymentInfo?.accumulationAmtWhenBuyConfirm,
      reserve_discount: accumulationUseAmt,
    })

    const orderer = orderInfoRef.current?.getValues?.()
    const receiver = orderAddressRef.current?.getValues?.()
    const couponData = orderProductRef.current?.getCoupons?.() ?? { productCoupon: {}, cartCoupon: null }

    const option = buildOrderOption({
      orderer,
      receiver,
      coupons: couponData,
      selectPayType,
      cardInfo,
      accumulationUseAmt,
      isAgreePayment,
    })

    requestPayment({
      selectPayType,
      cardInfo,
      myPayData: orderData?.myPayInfo,
      option,
      isAppView,
      isMobile,
      hasAccessToken,
    })
  }

  // 쿠폰 데이터 생성 함수
  const createCouponData = (productCouponObj: Record<string, any> = {}, cartCouponObj: any = null) => {
    const productCoupons = Object.entries(productCouponObj || {}).map(([key, value]) => {
      const [productNo] = key.split('_')
      return {
        couponIssueNo: value.couponIssueNo,
        productNo: Number(productNo),
      }
    })
    const cartCouponIssueNo = cartCouponObj?.couponIssueNo ?? null
    return {
      productCoupons,
      cartCouponIssueNo,
    }
  }

  // NOTE: 임시로 주석처리된 부분은 기존 로직이나 현재 적립금 사용 로직이 비효율적이어서 개선이 필요합니다.
  // const fetchPaymentInfo = async ({
  //   receiverZipCd,
  //   receiverAddress,
  //   receiverDetailAddress,
  //   accumulationUseAmt,
  //   productCoupon,
  //   cartCoupon,
  // }: {
  //   receiverZipCd: string
  //   receiverAddress: string
  //   receiverDetailAddress: string
  //   accumulationUseAmt: number
  //   productCoupon: any
  //   cartCoupon: any
  // }) => {
  //   if (!params.orderSheetNo || !receiverZipCd || !receiverAddress || !receiverDetailAddress) return
  //   try {
  //     let res
  //     const couponData = createCouponData(productCoupon, cartCoupon)
  //     if (hasAccessToken) {
  //       res = await postUserAddressCalculate(
  //         params.orderSheetNo,
  //         receiverZipCd,
  //         receiverAddress,
  //         receiverDetailAddress,
  //         receiverAddress + receiverDetailAddress,
  //         couponData,
  //         accumulationUseAmt
  //       )
  //     } else {
  //       res = await postGuestAddressCalculate(
  //         params.orderSheetNo,
  //         receiverZipCd,
  //         receiverAddress,
  //         receiverDetailAddress,
  //         receiverAddress + receiverDetailAddress
  //       )
  //     }
  //     setPaymentInfo(res.data.paymentInfo)
  //   } catch (err) {
  //     console.log(err)
  //     console.log('availableMaxAccumulationAmt >>> ', paymentInfo?.availableMaxAccumulationAmt)
  //     setAccumulationUseAmt(0)
  //     setPaymentInfo(defaultPaymentInfo)
  //     accumulationInputRef.current?.resetInput()
  //     accumulationInputRef.current?.focus()
  //   }
  // }

  // FIXME: 임시처리하였으나 속도가 느려 더 효율적인 방법 찾는중..
  const fetchPaymentInfo = async ({
    receiverZipCd,
    receiverAddress,
    receiverDetailAddress,
    accumulationUseAmt,
    productCoupon,
    cartCoupon,
  }: {
    receiverZipCd: string
    receiverAddress: string
    receiverDetailAddress: string
    accumulationUseAmt: number
    productCoupon: any
    cartCoupon: any
  }) => {
    if (!params.orderSheetNo || !receiverZipCd || !receiverAddress || !receiverDetailAddress) return

    const couponData = createCouponData(productCoupon, cartCoupon)
    const callCalculateApi = async (accAmt: number) => {
      if (!params.orderSheetNo) return
      if (hasAccessToken) {
        return await postUserAddressCalculate(
          params.orderSheetNo,
          receiverZipCd,
          receiverAddress,
          receiverDetailAddress,
          receiverAddress + receiverDetailAddress,
          couponData,
          accAmt
        )
      } else {
        return await postGuestAddressCalculate(
          params.orderSheetNo,
          receiverZipCd,
          receiverAddress,
          receiverDetailAddress,
          receiverAddress + receiverDetailAddress
        )
      }
    }

    try {
      const res = await callCalculateApi(accumulationUseAmt)
      if (!res || !res.data || !res.data.paymentInfo) return
      setPaymentInfo(res.data.paymentInfo)
      if (!trackedRef.current) {
        setIsToast(true)
        setToastMessage('최대 적립금과 쿠폰이 자동 적용되었습니다.')
      }
    } catch (err) {
      // 에러 발생 시 적립금 0으로 재시도
      try {
        setAccumulationUseAmt(0)
        accumulationInputRef.current?.setInputValue?.(0)
        const res = await callCalculateApi(0)
        if (!res || !res.data || !res.data.paymentInfo) return
        setPaymentInfo(res.data.paymentInfo)
        const maxAvailable = res.data.paymentInfo?.availableMaxAccumulationAmt ?? 0
        const myPoint = res.data.paymentInfo?.accumulationAmt ?? 0
        const maxByUnit = Math.floor(Math.min(maxAvailable, myPoint) / 1000) * 1000
        setAccumulationUseAmt(maxByUnit)
        accumulationInputRef.current?.setInputValue?.(maxByUnit)
        if (!trackedRef.current) {
          setIsToast(true)
          setToastMessage('최대 적립금과 쿠폰이 자동 적용되었습니다.')
        }
      } catch (err2) {
        setAccumulationUseAmt(0)
        setPaymentInfo(defaultPaymentInfo)
        accumulationInputRef.current?.resetInput?.()
        accumulationInputRef.current?.focus?.()
      }
    }
  }

  const handleOrderChange = () => {
    // 주소, 적립금 등 다른 ref에서 값 읽기
    const address = orderAddressRef.current?.getValues?.()
    // console.log('주소 정보:', address)
    // 적립금도 ref에서 읽는다면 const accumulationUseAmt = accumulationRef.current?.getAccumulation?.() ?? 0

    // OrderProductSection에서 쿠폰 정보 읽기 (타입 안전하게)
    const coupons = orderProductRef.current?.getCoupons?.() ?? { productCoupon: {}, cartCoupon: null }

    fetchPaymentInfo({
      receiverZipCd: address?.receiverZipCd ?? '',
      receiverAddress: address?.receiverAddress_1 ?? '',
      receiverDetailAddress: address?.receiverAddress_2 ?? '',
      accumulationUseAmt, // 필요하다면 ref에서 읽어온 값으로 교체
      productCoupon: coupons.productCoupon,
      cartCoupon: coupons.cartCoupon,
    })
  }

  const fetchOrderSheetCoupons = async (
    orderSheetNo: string,
    hasAccessToken: boolean
  ): Promise<{
    orderData: GetOrderSheetsOrderSheetNoResponseWithMyInfo
    couponsData: GetOrderSheetsOrderSheetNoCouponsResponse | null
  }> => {
    if (hasAccessToken) {
      const [orderRes, couponsRes] = await Promise.all([getOrderSheet(orderSheetNo), getSubmitCoupons(orderSheetNo)])
      return {
        orderData: orderRes.data as GetOrderSheetsOrderSheetNoResponseWithMyInfo,
        couponsData: couponsRes.data as GetOrderSheetsOrderSheetNoCouponsResponse,
      }
    } else {
      const orderRes = await getOrderSheet(orderSheetNo)
      return {
        orderData: orderRes.data as GetOrderSheetsOrderSheetNoResponseWithMyInfo,
        couponsData: null,
      }
    }
  }

  useEffect(() => {
    // 비회원이면 적립금 체크 제외
    if (!hasAccessToken) {
      if (!isAddressReady || !isProductReady) return
      handleOrderChange()
      return
    }
    if (!isAddressReady || !isProductReady || !isAccumulationReady) return
    handleOrderChange()
  }, [isAddressReady, isProductReady, isAccumulationReady, hasAccessToken, accumulationUseAmt])

  useEffect(() => {
    if (!hasAccessToken) return

    // 두 API를 병렬로 호출
    const fetchAll = async () => {
      try {
        // const [addressRes, profileRes] = await Promise.all([getRecentAddress(), getFishingProfile()])
        const [addressListRes, profileRes] = await Promise.all([getAddressList(), getFishingProfile()])
        // setUserDefaultAddress(addressRes.data)
        setUserDefaultAddress(addressListRes?.defaultAddress)
        setUserProfile(profileRes.data)
      } catch (err) {
        console.log(err)
      }
    }
    fetchAll()
  }, [hasAccessToken])

  useEffect(() => {
    const fetchOrderSheet = async () => {
      try {
        const orderSheetNo = params?.orderSheetNo
        if (!orderSheetNo) return

        const { orderData, couponsData } = await fetchOrderSheetCoupons(orderSheetNo, hasAccessToken)
        setOrderData(orderData)
        setOrderItems(orderData.deliveryGroups[0].orderProducts)
        setPaymentInfo(orderData.paymentInfo)
        setCoupons(couponsData)
      } catch (error) {
        console.error('주문서 조회 실패:', error)
      }
    }
    fetchOrderSheet()
  }, [hasAccessToken])

  // 1. 데이터 준비 완료 감지
  useEffect(() => {
    if (orderItems.length > 0 && categories && (hasAccessToken ? coupons : true)) {
      setIsTrackingReady(true)
    }
  }, [orderItems, categories, findCategory, coupons, hasAccessToken])

  // 2. 준비 완료 후 2초 뒤 트래킹 (딱 1번만)
  useEffect(() => {
    if (isTrackingReady && !trackedRef.current) {
      const timer = setTimeout(() => {
        trackBeginCheckout({
          isAppView,
          orderPrice: paymentInfo,
          cartArray: hasAccessToken
            ? toGTMItems(orderItems, categories, findCategory, coupons)
            : toGTMItems(orderItems, categories, findCategory),
          pay_back: paymentInfo?.accumulationAmtWhenBuyConfirm,
          reserve_discount: hasAccessToken ? accumulationUseAmt : 0,
        })
        trackAddShippingInfo({
          isAppView,
          orderPrice: paymentInfo,
          cartArray: hasAccessToken
            ? toGTMItems(orderItems, categories, findCategory, coupons)
            : toGTMItems(orderItems, categories, findCategory),
          reserve_discount: hasAccessToken ? accumulationUseAmt : 0,
        })
        trackedRef.current = true
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [isTrackingReady, paymentInfo, accumulationUseAmt, coupons])

  useEffect(() => {
    // 전역 스코프에 returnMypay 함수 정의
    window.returnMypay = function (paymentInfo: any) {
      if (paymentInfo.url) {
        const result = paymentInfo.url?.split('?')
        navigate(`/orderFinish?${result[1]}&paytype=${paymentInfo.payType}`)
      }
    }
    return () => {
      // 언마운트 시 전역 함수 제거(메모리 누수 방지)
      delete (window as any).returnMypay
    }
  }, [navigate])

  return (
    <>
      <div className='main_wrap'>
        <div className='contents_wrap'>
          <PageTitle>주문하기</PageTitle>
          <div className='order_wrap'>
            {/* 주문 상세 정보 */}
            <div className='left_area'>
              <Hr8 />
              <OrderUserInfoSection
                ref={orderInfoRef}
                hasAccessToken={hasAccessToken}
                initialName={userDefaultAddress?.receiverName || ''}
                initialPhone={userProfile?.mobilePhone || ''}
                initialEmail={userProfile?.email || ''}
              />
              <Hr8 />
              <OrderAddressSection
                ref={orderAddressRef}
                hasAccessToken={hasAccessToken}
                userDefaultAddress={userDefaultAddress}
                setUserDefaultAddress={setUserDefaultAddress}
                onReady={setIsAddressReady}
                onChange={
                  isAddressReady && isProductReady && isAccumulationReady
                    ? () => setTimeout(handleOrderChange, 0)
                    : false
                }
              />
              <Hr8 />
              <OrderProductSection
                ref={orderProductRef}
                orderItems={orderItems}
                paymentInfo={paymentInfo}
                coupons={coupons}
                onReady={setIsProductReady}
                onChange={isAddressReady && isProductReady && isAccumulationReady && handleOrderChange}
              />
              <Hr8 />
              {hasAccessToken && (
                <OrderAccumulationSection
                  ref={accumulationInputRef}
                  orderItems={orderItems}
                  paymentInfo={paymentInfo}
                  isPointChanged={isPointChanged}
                  setIsPointChanged={setIsPointChanged}
                  setAccumulationUseAmt={setAccumulationUseAmt}
                  onReady={setIsAccumulationReady}
                />
              )}

              <Hr8 />
              <PaymentMethodSection
                orderData={orderData}
                setSelectPayType={setSelectPayType}
                setCardInfo={setCardInfo}
              />
            </div>

            {/* 주문 가격 정보 */}
            <div className='right_area'>
              <OrderSummarySection
                paymentInfo={paymentInfo}
                accumulationUseAmt={accumulationUseAmt}
                selectPayType={selectPayType}
                isAgreePayment={isAgreePayment}
                setIsAgreePayment={setIsAgreePayment}
                isAgreeNonMemberPrivacy={isAgreeNonMemberPrivacy}
                setIsAgreeNonMemberPrivacy={setIsAgreeNonMemberPrivacy}
                showAgreement={showAgreement}
                setShowAgreement={setShowAgreement}
                onPurchase={handlePurchase}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NewOrder
